// src/lib/feasibility.js
//
// Single source of truth for booking FEASIBILITY math — weight (payload) and
// baggage (piece-count) — shared by BOTH the funnel prefilter (App.jsx feasCheck)
// and the server backstop (bookings-create.js). Pure, no React, no I/O, so it is
// unit-testable in isolation and the client/server can never diverge.
//
// Two layers (an aircraft is feasible only if BOTH pass):
//   SCHICHT 1 — WEIGHT: pax body + crew + bag weights + pets <= availablePayload.
//   SCHICHT 2 — BAGGAGE: weighted hold-slot count <= maxLuggage, with a carry-on
//              waterfall (compartment first, then free laps).
//
// Crew/pilot weights and luggage weights have sensible DEFAULTS here; callers
// that carry CMS overrides (App.jsx siteSettings.pilotWeightKg / luggageWeights)
// pass them in via the options bag so the same code respects per-site config.

// ── Crew ────────────────────────────────────────────────────────────────────
export const DEFAULT_PILOT_WEIGHT_KG = 90;         // always-present captain
export const DEFAULT_SECOND_PILOT_WEIGHT_KG = 85;  // mandatory / added second pilot

// True when the aircraft inherently flies two pilots (mandatory crew). Defensive:
// missing flags → single-pilot. Mirrors the funnel + canBookPilotExtra rule.
export function requiresMandatorySecondPilot(aircraft) {
  return !!(aircraft && (aircraft.requiresTwoPilots || (Number(aircraft.minCrew) || 1) >= 2));
}

// Crew mass counted against payload. 1st pilot always; 2nd pilot when the
// aircraft requires two OR the customer added the 2nd-pilot extra. Eligibility
// (availablePayload) uses mandatory-only (secondPilotSelected=false) because the
// optional 2nd pilot isn't chosen until Step 3.
export function crewWeight(aircraft, secondPilotSelected = false, opts = {}) {
  const pilot = opts.pilotWeightKg != null ? opts.pilotWeightKg : DEFAULT_PILOT_WEIGHT_KG;
  const second = opts.secondPilotWeightKg != null ? opts.secondPilotWeightKg : DEFAULT_SECOND_PILOT_WEIGHT_KG;
  const hasSecond = requiresMandatorySecondPilot(aircraft) || !!secondPilotSelected;
  return pilot + (hasSecond ? second : 0);
}

// ── Payload (SCHICHT 1) ──────────────────────────────────────────────────────
//
// Kilograms available for PAX + LUGGAGE after crew is subtracted. maxPayload is
// defined as "useful load at max fuel, available for crew + pax + luggage", so we
// remove the mandatory crew here.
//
// ┌─ FUTURE FUEL-AWARE SEAM ───────────────────────────────────────────────────┐
// │ `flightMin` is accepted now but UNUSED in this stage-1 implementation. A     │
// │ later upgrade (fuel-burn vs distance, or two-point payload/range             │
// │ interpolation) changes ONLY this function body — every caller already        │
// │ threads flightMin, so no call-site refactor is needed. Do not add fuel       │
// │ logic at call sites; add it HERE.                                            │
// └─────────────────────────────────────────────────────────────────────────────┘
export function availablePayload(aircraft, opts = {}) {
  // eslint-disable-next-line no-unused-vars
  const _flightMin = opts.flightMin; // reserved — see FUTURE FUEL-AWARE SEAM above
  const maxPayload = Number(aircraft?.maxPayload) || 0;
  return maxPayload - crewWeight(aircraft, false, opts);
}

// Default luggage piece weights (kg). Overridable via siteSettings.luggageWeights.
export const DEFAULT_LUG_WEIGHT = { carryOn: 5, small: 8, standard: 23, large: 32 };

export function luggageWeight(lug, weights = DEFAULT_LUG_WEIGHT) {
  return (lug?.carryOn || 0) * weights.carryOn
       + (lug?.small || 0) * weights.small
       + (lug?.standard || 0) * weights.standard
       + (lug?.large || 0) * weights.large;
}

// SCHICHT 1 predicate. `needKg` = pax body + bag weights + pets (crew is NOT in
// needKg — it is subtracted inside availablePayload). Zero need always fits
// (no weight entered yet → don't pre-exclude).
export function weightFits(aircraft, { needKg = 0, flightMin, pilotWeightKg, secondPilotWeightKg } = {}) {
  const avail = availablePayload(aircraft, { flightMin, pilotWeightKg, secondPilotWeightKg });
  return { ok: needKg === 0 || needKg <= avail, needKg, avail };
}

// ── Baggage (SCHICHT 2) ──────────────────────────────────────────────────────
//
// Weighted COMPARTMENT slots per hold piece, and the carry-on slot cost. maxLuggage
// is the aircraft's slot budget (an integer "bags" figure).
export const HOLD_SLOTS = { large: 1.5, standard: 1.0, small: 0.5 };
export const CARRYON_SLOT = 0.25;
const EPS = 1e-9;

// Does the baggage fit?
//   1. Hold pieces (large/standard/small) consume weighted slots → must be <= maxLuggage.
//   2. Carry-ons (0.25 slots each) fill the REMAINING compartment slots first.
//   3. Any carry-on overflow must each occupy a FREE LAP. Free laps = seated pax
//      (adults + children) minus infants (each infant already occupies one lap).
// Returns { ok, reason, detail }. reason ∈ '', 'hold-over', 'carryon-over'.
export function baggageFits(aircraft, { carryOn = 0, small = 0, standard = 0, large = 0, seatedPax = 0, infants = 0 } = {}) {
  const maxLuggage = Number(aircraft?.maxLuggage) || 0;
  if (!(maxLuggage > 0)) return { ok: false, reason: 'no-capacity', detail: { maxLuggage } };

  const holdSlots = large * HOLD_SLOTS.large + standard * HOLD_SLOTS.standard + small * HOLD_SLOTS.small;
  if (holdSlots > maxLuggage + EPS) {
    return { ok: false, reason: 'hold-over', detail: { holdSlots, maxLuggage } };
  }

  const remainingSlots = maxLuggage - holdSlots;
  const carryOnInHold = Math.min(carryOn, Math.floor(remainingSlots / CARRYON_SLOT + EPS));
  const overflow = carryOn - carryOnInHold;
  const freeLaps = Math.max(0, seatedPax - infants);
  if (overflow > freeLaps) {
    return { ok: false, reason: 'carryon-over', detail: { overflow, freeLaps } };
  }
  return { ok: true, reason: '', detail: { holdSlots, maxLuggage, overflow, freeLaps } };
}

// True when the aircraft has a usable luggage-compartment budget. Used by the
// shared bookability layer (aircraft-availability.js) — an aircraft with no
// maxLuggage cannot have its baggage validated, so it is not sellable (POR),
// exactly like an aircraft with no valid cancellation policy.
export function aircraftHasBaggageCapacity(aircraft) {
  return (Number(aircraft?.maxLuggage) || 0) > 0;
}
